import { useEffect, useState } from 'react'
import { Input, HiddenMenu } from 'components'
import { parse, format, isValid } from 'date-fns'
import Icon from 'components/priority/Icon/Icon'
import Calendar from 'components/global/Calendar/Calendar'

export const DateInput = ({
  date: [date, setDate] = [null, () => {}],
  left,
  margin,
  width,
  disabledDays,
  ...props
}) => {
  // initial state
  const [showCalendar, setShowCalendar] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const isValidDate = (date) =>
    !(Object.prototype.toString.call(date) === '[object Date]' && isNaN(date))

  // sets the date upon leaving the input, or if no date is set
  useEffect(() => {
    const calendarIsHidden = showCalendar === false
    if (calendarIsHidden) {
      const formattedDate = date ? format(date, 'MM/dd/yyyy') : ''
      setInputValue(formattedDate)
    }
    // eslint-disable-next-line
  }, [showCalendar])

  // when the inputValue is a valid date, set the date accordingly & set to default with new case
  useEffect(() => {
    if (!date) {
      setInputValue('')
    }
    const [month, day, year] = inputValue.split('/')
    const hasCompleteDate = month && day && year?.length === 4
    const inputReady = inputValue === '' || hasCompleteDate
    const dateExists = date
    const shouldSetInputValue = inputReady && dateExists
    if (shouldSetInputValue) {
      setInputValue(format(date, 'MM/dd/yyyy'))
    }
    // eslint-disable-next-line
  }, [date])

  if (!isValidDate(date)) {
    return <></>
  }

  // gets the current day, month, and year
  const currentDate = format(new Date(), 'MM/dd/yyyy')
  const [currentMonth, currentDay, currentYear] = currentDate.split('/')
  const currentShorthandYear = currentYear.slice(-2)

  // confirms the input value is a numeric date
  const isDate = (date) => {
    const cleanDate = date.split('/').join('')
    return +cleanDate === +cleanDate * 1
  }

  // confirms the date is valid
  const parseDate = (dateString, format) => {
    //parse function cannot handle a month 0
    const userStartsMonthWithZero =
      dateString[0] === '0' && dateString[1] === '/'

    if (userStartsMonthWithZero) {
      const originalDateString = dateString
      dateString =
        originalDateString.slice(0, 1) +
        `${currentMonth[1]}` +
        originalDateString.slice(1)
    }

    //parse function cannot handle a day 0
    const userStartsDayWithZero = dateString[3] === '0' && dateString[4] === '/'

    if (userStartsDayWithZero) {
      const originalDateString = dateString
      dateString =
        originalDateString.slice(0, 4) + '1' + originalDateString.slice(4)
    }

    const parsed = parse(dateString, format, new Date())
    return isValid(parsed) && parsed
  }

  // determines the date upon each onChange within the input
  const determineDate = (value) => {
    // gets the input's day, month, and year
    const valueCanBeSplit = value && typeof value === 'string'
    const [inputMonth, inputDay, inputYear] = valueCanBeSplit
      ? value.split('/')
      : []
    // determines if the year is 2 or 4 characters long
    const yearIsShorthand = inputYear?.length === 2
    const yearIsFull = inputYear?.length === 4

    // if the year is 2 characters long, create a 4 character year
    const shorthandYear =
      yearIsShorthand &&
      `${inputYear <= currentShorthandYear ? '20' : '19'}${inputYear}`

    // determine the full year
    let yearResult = currentYear
    if (yearIsShorthand) {
      yearResult = shorthandYear
    } else if (yearIsFull) {
      yearResult = inputYear
    }

    // set the date string
    const incomingDate = `${inputMonth || currentMonth}/${
      inputDay || currentDay
    }/${yearResult}`

    // parse the date string, and confirm it is a valid date
    const parsedDate = parseDate(incomingDate, 'MM/dd/yyyy')

    // set the state accordingly
    setInputValue(value)
    setDate(value && parsedDate)
    setShowCalendar(value && true)
  }

  return (
    <HiddenMenu
      show={[showCalendar, setShowCalendar]}
      left={left}
      width={width}
      margin={margin}
      parentElement={() => (
        <Input
          onChange={({ target }) =>
            isDate(target.value) && determineDate(target.value)
          }
          onEnter={() => setShowCalendar(false)}
          onKeyDown={({ key }) =>
            (key === 'Tab' || key === 'Enter' || key === 'Escape') &&
            setShowCalendar(false)
          }
          onClick={() => date && setShowCalendar(true)}
          placeholder="mm/dd/yyyy"
          padding="12px 36px 12px 12px"
          elementRight={() => (
            <Icon
              icon="calendar"
              fill={showCalendar ? 'gray1' : 'gray2'}
              hoverfill="gray1"
              onClick={() => setShowCalendar(!showCalendar)}
            />
          )}
          value={[inputValue, setInputValue]}
          {...props}
        />
      )}
      hiddenElement={() => (
        <Calendar
          position="absolute"
          top="12px"
          right={left && '0px'}
          date={[date, setDate]}
          show={[showCalendar, setShowCalendar]}
          disabledDays={disabledDays}
        />
      )}
    />
  )
}

export default DateInput
